import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { ChargingStationLocationDetailsComponent } from 'src/app/features/charging-station-location/charging-station-location-details/charging-station-location-details.component';
import { Department, DepartmentSearchEvent } from 'src/app/features/department/department.model';
import IncidentService from 'src/app/shared/services/incident.service';
import OrganizationService from 'src/app/shared/services/organization.service';
import { saveAs } from 'file-saver';
import { DepartmentService } from 'src/app/shared/services/department.service';
import { ChargingStationLocationService } from 'src/app/shared/services/charging-station-location.service';
import {
  ChargingStationLocation,
  ChargingStationLocationSearchEvent,
} from 'src/app/features/charging-station-location/charging-station-location-model';
import { PageEvent } from '@angular/material/paginator';
import { Organization, OrganizationSearchEvent } from 'src/app/features/organization/organization-models';
import { MatDialogTitle, MatDialogClose, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatSuffix, MatHint } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';

@Component({
  selector: 'adm-incident-download-report',
  templateUrl: './incident-download-report-dialog.component.html',
  styleUrls: ['./incident-download-report-dialog.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogClose,
    MatDialogContent,
    MatDialogActions,
    MatIcon,
    MatButton,
    MatFormField,
    MatSuffix,
    MatHint,
    MatDatepickerModule,
    MatSelect,
    MatOption,
    CustomDropDownArrowDirective,
  ],
})
export class IncidentDownloadReportComponent implements OnInit {
  dialogRef = inject<MatDialogRef<IncidentDownloadReportComponent>>(MatDialogRef);
  private incidentService = inject(IncidentService);
  private organizationService = inject(OrganizationService);
  private departmentService = inject(DepartmentService);
  private chargingStationLocationService = inject(ChargingStationLocationService);

  public loading: boolean;
  public organizationBadgeCount = 0;
  public departmentBadgeCount = 0;
  public locationBadgeCount = 0;
  public organizations: Organization[];
  public departments: Department[];
  public allDepartments: Department[] = [];
  public locations: ChargingStationLocation[];
  public allLocations: ChargingStationLocation[] = [];
  public pageEvent: PageEvent = new PageEvent();
  public chargingStationLocationSearchEvent: ChargingStationLocationSearchEvent = new ChargingStationLocationSearchEvent(this.pageEvent);

  public downloadReportFormGroup: FormGroup = new FormGroup({
    selectedOrganizations: new FormControl<Organization[]>([]),
    selectedDepartments: new FormControl<Department[]>([]),
    selectedLocations: new FormControl<ChargingStationLocationDetailsComponent[]>([]),
    startDate: new FormControl(moment().startOf('day'), [Validators.required]),
    endDate: new FormControl(moment().startOf('day'), [Validators.required]),
  });

  get selectedOrganizations() {
    return this.downloadReportFormGroup.controls['selectedOrganizations'];
  }

  get selectedDepartments() {
    return this.downloadReportFormGroup.controls['selectedDepartments'];
  }

  get selectedLocations() {
    return this.downloadReportFormGroup.controls['selectedLocations'];
  }

  async ngOnInit(): Promise<void> {
    this.pageEvent.pageSize = 25;
    this.chargingStationLocationSearchEvent.paging = this.pageEvent;
    await this.getOrganizations();
    await this.getDepartments();
    await this.getLocations();

    this.downloadReportFormGroup.valueChanges.subscribe(() => {
      this.updateCounters();
    });
  }

  public hasSelectedOrganizations() {
    return this.selectedOrganizations.value.filter(o => o.id > 0).length > 0;
  }

  public hasSelectedDepartments() {
    return this.selectedDepartments.value.length > 0;
  }

  public hasSelectedLocations() {
    return this.selectedLocations.value.length > 0;
  }

  private async getOrganizations() {
    const searchEvent: OrganizationSearchEvent = {
      paging: new PageEvent(),
      sorting: null,
    };
    searchEvent.paging.pageIndex = 0;
    searchEvent.paging.pageSize = 10000;
    this.organizationService.getOrganizations(searchEvent).subscribe({
      next: result => {
        this.organizations = result.items;
      },
    });
  }

  public async getDepartments() {
    this.reset('departments');

    if (this.allDepartments.length === 0) {
      const searchEvent = new DepartmentSearchEvent();
      const pageEvent = new PageEvent();
      pageEvent.pageSize = 1000;
      searchEvent.paging = pageEvent;

      const fetchedDepartments = await firstValueFrom(this.departmentService.getDepartments(searchEvent));
      this.allDepartments = fetchedDepartments.items;
    }

    if (this.hasSelectedOrganizations()) {
      this.departments = this.allDepartments.filter(l => this.selectedOrganizations.value.map(o => o.id).includes(l.organizationId));
    }

    if (this.departments.length === 0) {
      this.departments = this.allDepartments;
    }
  }

  public async getLocations() {
    this.reset('locations');

    if (this.allLocations.length === 0) {
      const fetchedLocations = await firstValueFrom(
        this.chargingStationLocationService.getChargingStationLocations(this.chargingStationLocationSearchEvent)
      );
      this.allLocations = fetchedLocations.items;
    }

    if (this.hasSelectedOrganizations()) {
      this.locations = this.allLocations.filter(l => this.selectedOrganizations.value.map(o => o.id).includes(l.organizationId));
    }
    if (this.hasSelectedDepartments()) {
      this.locations = this.allLocations.filter(l => this.selectedDepartments.value.map(d => d.id).includes(l.departmentId));
    }

    if (this.locations.length === 0) {
      this.locations = this.allLocations;
    }
  }

  public cancel() {
    this.dialogRef.close();
  }

  async reset(typeToReset: string, event?: Event) {
    switch (typeToReset) {
      case 'organizations':
        this.selectedOrganizations.setValue([]);
        this.departments = this.allDepartments;
        this.locations = this.allLocations;
        break;
      case 'departments':
        this.selectedDepartments.setValue([]);
        this.departments = this.allDepartments;
        this.locations = this.allLocations;
        break;
      case 'locations':
        this.selectedLocations.setValue([]);
        this.locations = this.allLocations;
        break;
      default:
        break;
    }
    if (event) {
      event.stopPropagation();
    }
    this.updateCounters();
  }

  private updateCounters() {
    this.organizationBadgeCount = this.selectedOrganizations.value.filter(o => o.id > 0).length;
    this.departmentBadgeCount = this.selectedDepartments.value.length;
    this.locationBadgeCount = this.selectedLocations.value.length;
  }

  public downloadReport() {
    this.loading = true;

    const downloadIncidentReportModel = {
      from: moment(this.downloadReportFormGroup.value.startDate).format(),
      to: moment(this.downloadReportFormGroup.value.endDate).add(23, 'h').add(59, 'm').add(59, 's').format(),
      organizations: this.selectedOrganizations.value?.map(l => l.id),
      departments: this.selectedDepartments.value?.map(l => l.id),
      locations: this.selectedLocations.value?.map(l => l.identifier),
    };

    this.incidentService
      .downloadIncidentReport(downloadIncidentReportModel)
      .subscribe({
        next: result => {
          const fileName =
            'Driftstatusrapport fra ' +
            moment(downloadIncidentReportModel.from).format('DD-MM-YYYY') +
            ' til ' +
            moment(downloadIncidentReportModel.to).format('DD-MM-YYYY') +
            '.xlsx';

          saveAs(result, fileName);
        },
      })
      .add(() => {
        this.loading = false;
      });
  }
}
